<template>
  <div class="wf-form-renderer">
    <!-- 编辑态 -->
    <el-form
      v-if="!readonly"
      ref="elForm"
      :model="form"
      :rules="rules"
      :label-width="labelWidth"
      size="small"
      :disabled="disabled"
    >
      <el-form-item
        v-for="f in fields"
        :key="f.key"
        :label="f.label"
        :prop="f.key"
        v-show="f.type !== 'tips' && f.visible !== false"
      >
        <!-- 单行文本 -->
        <el-input
          v-if="f.type === 'input'"
          v-model="form[f.key]"
          :placeholder="f.placeholder || '请输入' + (f.label || '')"
          clearable
        />
        <!-- 多行文本 -->
        <el-input
          v-else-if="f.type === 'textarea'"
          v-model="form[f.key]"
          type="textarea"
          :rows="3"
          :placeholder="f.placeholder || '请输入' + (f.label || '')"
        />
        <!-- 数字 -->
        <el-input-number
          v-else-if="f.type === 'number'"
          v-model="form[f.key]"
          controls-position="right"
          :placeholder="f.placeholder"
          style="width: 220px"
        />
        <!-- 金额：千分位显示 + 人民币大写 -->
        <div v-else-if="f.type === 'money'" class="wf-money-wrap">
          <span class="wf-money">
            <el-input
              :value="moneyValue(f)"
              :placeholder="f.placeholder || '请输入金额'"
              clearable
              style="width: 220px"
              @input="onMoneyInput(f, $event)"
              @focus="onMoneyFocus(f)"
              @blur="onMoneyBlur(f)"
            />
            <em v-if="f.unit" class="wf-unit">{{ f.unit }}</em>
          </span>
          <div v-if="f.showUpper !== false && hasMoneyValue(f)" class="wf-money-upper">
            大写：{{ toChineseAmount(form[f.key]) }}
          </div>
        </div>
        <!-- 下拉 -->
        <el-select
          v-else-if="f.type === 'select'"
          v-model="form[f.key]"
          :multiple="!!f.multiple"
          :placeholder="f.placeholder || '请选择'"
          clearable
          style="width: 260px"
        >
          <el-option
            v-for="o in f.options || []"
            :key="o.value"
            :label="o.label"
            :value="o.value"
          />
        </el-select>
        <!-- 单选 -->
        <el-radio-group v-else-if="f.type === 'radio'" v-model="form[f.key]">
          <el-radio v-for="o in f.options || []" :key="o.value" :label="o.value">{{ o.label }}</el-radio>
        </el-radio-group>
        <!-- 多选 -->
        <el-checkbox-group v-else-if="f.type === 'checkbox'" v-model="form[f.key]">
          <el-checkbox v-for="o in f.options || []" :key="o.value" :label="o.value">{{ o.label }}</el-checkbox>
        </el-checkbox-group>
        <!-- 级联 -->
        <el-cascader
          v-else-if="f.type === 'cascade'"
          v-model="form[f.key]"
          :options="cascadeOptions(f)"
          :props="{ expandTrigger: 'hover' }"
          :placeholder="f.placeholder || '请选择'"
          clearable
          filterable
          style="width: 320px"
        />
        <!-- 当前用户id -->
        <el-input
          v-else-if="f.type === 'curUser'"
          :value="curUserDisplay(f)"
          disabled
          placeholder="自动获取当前登录用户"
          style="width: 260px"
        />
        <!-- 用户角色：自动获取当前登录用户角色 -->
        <el-input
          v-else-if="f.type === 'userRole'"
          :value="userRoleDisplay(f)"
          disabled
          placeholder="自动获取当前登录用户角色"
          style="width: 260px"
        />
        <!-- 总价：自动计算 -->
        <el-input
          v-else-if="f.type === 'total'"
          :value="totalDisplay(f)"
          disabled
          style="width: 220px"
        >
          <template v-if="f.unit" slot="suffix">{{ f.unit }}</template>
        </el-input>
        <!-- 取值：自定义SQL首行值 -->
        <el-input
          v-else-if="f.type === 'sqlValue'"
          :value="form[f.key]"
          disabled
          placeholder="自动取值(自定义SQL)"
          style="width: 260px"
        />
        <!-- 受控数字 -->
        <el-input-number
          v-else-if="f.type === 'ctrlNumber'"
          v-model="form[f.key]"
          controls-position="right"
          style="width: 220px"
        />
        <!-- 日期 -->
        <el-date-picker
          v-else-if="f.type === 'date'"
          v-model="form[f.key]"
          type="date"
          value-format="yyyy-MM-dd"
          :placeholder="f.placeholder || '请选择日期'"
          style="width: 220px"
        />
        <!-- 日期区间 -->
        <el-date-picker
          v-else-if="f.type === 'daterange'"
          v-model="form[f.key]"
          type="daterange"
          value-format="yyyy-MM-dd"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
        />
        <!-- 成员（配置「部门范围」时按关联部门字段过滤，含子部门） -->
        <el-select
          v-else-if="f.type === 'user'"
          v-model="form[f.key]"
          :multiple="!!f.multiple"
          filterable
          :placeholder="f.placeholder || '请选择成员'"
          :no-data-text="deptScopeIds(f) ? '所选部门下暂无成员' : '暂无数据'"
          clearable
          style="width: 260px"
        >
          <el-option
            v-for="u in userOptionsFor(f)"
            :key="u.id"
            :label="u.trueName || u.name"
            :value="u.id"
          />
        </el-select>
        <!-- 部门（层级选择：中心 / 部门；停用的部门、中心不参与选择；
             配置「中心范围」时中心只能展开、不可勾选，表单值一定是具体部门） -->
        <el-cascader
          v-else-if="f.type === 'dept'"
          v-model="form[f.key]"
          :options="deptTreeMap[f.key]"
          :props="deptCascaderPropsMap[f.key]"
          :placeholder="f.placeholder || '请选择部门'"
          filterable
          clearable
          style="width: 260px"
        />
        <!-- 商机 -->
        <el-select
          v-else-if="f.type === 'chance'"
          v-model="form[f.key]"
          filterable
          :placeholder="f.placeholder || '请选择商机'"
          clearable
          style="width: 260px"
        >
          <el-option
            v-for="c in chanceOptionsFor(f)"
            :key="c.id"
            :label="saleOptionLabel(c)"
            :value="c.id"
          />
        </el-select>
        <!-- 项目 -->
        <el-select
          v-else-if="f.type === 'project'"
          v-model="form[f.key]"
          filterable
          :placeholder="f.placeholder || '请选择项目'"
          clearable
          style="width: 260px"
        >
          <el-option
            v-for="p in projectOptionsFor(f)"
            :key="p.chanceId"
            :label="saleOptionLabel(p)"
            :value="p.chanceId"
          />
        </el-select>
        <!-- 附件 -->
        <span v-else-if="f.type === 'upload'" class="wf-upload-wrap">
          <el-upload
            action="#"
            :accept="acceptOf(f)"
            :http-request="customUpload"
            :before-upload="file => beforeUploadFile(f, file)"
            :file-list="form[f.key] || []"
            :on-success="(res, file, files) => onUploadSuccess(f, res, files)"
            :on-remove="(file, files) => onUploadRemove(f, files)"
            :on-error="onUploadError"
            multiple
          >
            <el-button size="small" type="primary" icon="el-icon-upload2">点击上传</el-button>
          </el-upload>
          <div v-if="uploadTip(f)" class="wf-upload-tip">支持格式：{{ uploadTip(f) }}</div>
        </span>
        <!-- 明细/表格 -->
        <div v-else-if="f.type === 'detail'" class="wf-detail">
          <table class="wf-detail-table">
            <colgroup>
              <col class="wf-detail-col-fixed" />
              <col v-for="c in f.columns || []" :key="'col_' + c.key" :style="detailColStyle(f, c)" />
              <col class="wf-detail-col-fixed" />
            </colgroup>
            <thead>
              <tr>
                <th class="wf-detail-idx">#</th>
                <th v-for="(c, ci) in f.columns || []" :key="c.key">
                  <em v-if="c.required" class="wf-detail-req">*</em>{{ c.label }}<span v-if="c.type === 'money' && c.unit" class="wf-detail-unit">({{ c.unit }})</span>
                  <i
                    v-if="resizable && !disabled"
                    class="wf-col-resizer"
                    :class="{ active: resizingKey === f.key + '_' + ci }"
                    title="拖动调整列宽"
                    @mousedown.stop.prevent="startColResize(f, ci, $event)"
                    @click.stop
                  ></i>
                </th>
                <th class="wf-detail-idx">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, ri) in (form[f.key] || [])" :key="ri">
                <td class="wf-detail-idx">{{ ri + 1 }}</td>
                <td v-for="c in f.columns || []" :key="c.key" class="wf-detail-cell">
                  <el-input v-if="c.type === 'input'" v-model="row[c.key]" size="mini" :placeholder="c.label" />
                  <el-input
                    v-else-if="c.type === 'textarea'"
                    v-model="row[c.key]"
                    type="textarea"
                    size="mini"
                    :autosize="{ minRows: 1, maxRows: 4 }"
                    :placeholder="c.label"
                  />
                  <el-input-number
                    v-else-if="c.type === 'number'"
                    v-model="row[c.key]"
                    size="mini"
                    controls-position="right"
                    :placeholder="c.label"
                    style="width: 100%"
                  />
                  <el-input
                    v-else-if="c.type === 'money'"
                    :value="detailMoneyValue(f, ri, c)"
                    size="mini"
                    :placeholder="c.label"
                    style="width: 100%"
                    @input="onDetailMoneyInput(f, ri, c, $event)"
                    @focus="onDetailMoneyFocus(f, ri, c)"
                    @blur="onDetailMoneyBlur(f, ri, c)"
                  />
                  <el-date-picker
                    v-else-if="c.type === 'date'"
                    v-model="row[c.key]"
                    type="date"
                    size="mini"
                    value-format="yyyy-MM-dd"
                    :placeholder="c.label"
                    style="width: 100%"
                  />
                  <el-cascader
                    v-else-if="c.type === 'cascade'"
                    v-model="row[c.key]"
                    :options="cascadeOptions(c)"
                    :props="{ expandTrigger: 'hover' }"
                    size="mini"
                    clearable
                    filterable
                    :placeholder="c.label"
                    style="width: 100%"
                  />
                  <el-input
                    v-else-if="c.type === 'sqlValue'"
                    :value="row[c.key]"
                    size="mini"
                    disabled
                    placeholder="自动取值"
                  />
                  <!-- 商机子列 -->
                  <el-select
                    v-else-if="c.type === 'chance'"
                    v-model="row[c.key]"
                    size="mini"
                    filterable
                    clearable
                    :placeholder="c.label"
                    style="width: 100%"
                  >
                    <el-option
                      v-for="ch in chanceOptionsFor(c)"
                      :key="ch.id"
                      :label="saleOptionLabel(ch)"
                      :value="ch.id"
                    />
                  </el-select>
                  <!-- 项目子列 -->
                  <el-select
                    v-else-if="c.type === 'project'"
                    v-model="row[c.key]"
                    size="mini"
                    filterable
                    clearable
                    :placeholder="c.label"
                    style="width: 100%"
                  >
                    <el-option
                      v-for="p in projectOptionsFor(c)"
                      :key="p.chanceId"
                      :label="saleOptionLabel(p)"
                      :value="p.chanceId"
                    />
                  </el-select>
                  <el-input-number
                    v-else-if="c.type === 'ctrlNumber'"
                    v-model="row[c.key]"
                    size="mini"
                    controls-position="right"
                    style="width: 100%"
                  />
                  <el-input v-else v-model="row[c.key]" size="mini" :placeholder="c.label" />
                </td>
                <td class="wf-detail-idx">
                  <i class="el-icon-delete wf-detail-del" title="删除该行" @click="removeDetailRow(f, ri)"></i>
                </td>
              </tr>
              <tr v-if="!(form[f.key] || []).length">
                <td :colspan="(f.columns || []).length + 2" class="wf-detail-empty">暂无明细行，点击下方「增加明细」添加</td>
              </tr>
            </tbody>
          </table>
          <div class="wf-detail-add" @click="addDetailRow(f)">+ 增加明细</div>
        </div>
        <!-- 只读文本：设计时写入的内容，任何人不可编辑，纯文本展示 -->
        <span v-else-if="f.type === 'readonlyText'" class="wf-ro-value">{{ readonlyTextValue(f) || '—' }}</span>
        <!-- 兜底：未知类型按文本 -->
        <el-input v-else v-model="form[f.key]" :placeholder="f.placeholder" clearable />
      </el-form-item>

      <!-- 说明文字 -->
      <template v-for="f in fields">
        <div v-if="f.type === 'tips'" :key="'tips_' + f.key" class="wf-tips">
          <i class="el-icon-info"></i> {{ f.content || f.label }}
        </div>
      </template>
    </el-form>

    <!-- 只读态 -->
    <el-form v-else :label-width="labelWidth" size="small" class="wf-readonly">
      <el-form-item v-for="f in visibleFields" :key="f.key" :label="f.label">
        <!-- 附件：文件名 + 下载 -->
        <div v-if="f.type === 'upload'" class="wf-ro-files">
          <div v-for="(file, i) in (form[f.key] || [])" :key="i" class="wf-ro-file">
            <i class="el-icon-document"></i>
            <span class="wf-ro-file-name" :title="file.name">{{ file.name }}</span>
            <el-button
              type="text"
              icon="el-icon-download"
              :loading="downloading === String(file.id)"
              @click="downloadFile(file)"
            >下载</el-button>
          </div>
          <span v-if="!(form[f.key] || []).length" class="wf-ro-value">—</span>
        </div>
        <!-- 明细：表格回显 -->
        <div v-else-if="f.type === 'detail'" class="wf-ro-detail">
          <el-table v-if="(form[f.key] || []).length" :data="form[f.key]" size="mini" border>
            <el-table-column type="index" label="#" width="42" align="center" />
            <el-table-column
              v-for="c in f.columns || []"
              :key="c.key"
              :width="c.width || ''"
              :label="c.type === 'money' && c.unit ? c.label + '(' + c.unit + ')' : c.label"
            >
              <template slot-scope="{ row }">{{ detailCellText(c, row[c.key]) }}</template>
            </el-table-column>
          </el-table>
          <span v-else class="wf-ro-value">—</span>
        </div>
        <template v-else>
          <span class="wf-ro-value">{{ displayText(f) }}</span>
          <div v-if="f.type === 'money' && f.showUpper !== false && hasMoneyValue(f)" class="wf-money-upper">
            大写：{{ toChineseAmount(form[f.key]) }}
          </div>
        </template>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import request from '@/utils/request3'
import { queryDataset, querySqlValue } from '@/api/wflow/form'
import { buildCascadeTree, resolveCascadeLabels, findCascadePath } from './cascadeUtil'
import { labelWidthOf } from './labelWidth'
import { delcommafy, moneyInputFormatter, formatMoney, toMoneyNumber, toChineseAmount } from '@/utils/money'
import { attachAccept, attachAllowedExts, attachTipText } from './designer/form/fieldTypes'

export default {
  name: 'FormRenderer',
  props: {
    // 表单 schema：对象 {fields:[]} 或 JSON 字符串
    schema: { type: [Object, String], default: () => ({ fields: [] }) },
    // 表单数据（v-model）
    value: { type: Object, default: () => ({}) },
    readonly: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    // 成员/部门下拉数据源（由父组件提供）
    userOptions: { type: Array, default: () => [] },
    deptOptions: { type: Array, default: () => [] },
    // 商机下拉数据源（由父组件提供）
    chanceOptions: { type: Array, default: () => [] },
    // 本人商机数据源（scope='mine' 的商机字段使用，由父组件提供）
    myChanceOptions: { type: Array, default: () => [] },
    // 项目下拉数据源（由父组件提供）
    projectOptions: { type: Array, default: () => [] },
    // 本人项目数据源（scope='mine' 的项目字段使用，由父组件提供）
    myProjectOptions: { type: Array, default: () => [] },
    // 角色列表数据源（userRole 字段展示角色名称，由父组件提供）
    roleOptions: { type: Array, default: () => [] },
    // 附件上传地址
    uploadAction: { type: String, default: '' },
    // 明细表格：是否允许拖动表头调整列宽（仅本次会话生效，不回写表单定义）
    resizable: { type: Boolean, default: false }
  },
  data() {
    return {
      form: {},
      internal: false,
      downloading: '',
      // 级联数据集缓存：sql -> 行数组
      cascadeRows: {},
      cascadeFailed: {},
      // 取值字段：引用参数签名与防抖定时器
      sqlValueSig: {},
      sqlValueTimers: {},
      // 明细联动填充：来源参数签名与防抖定时器（key = 明细字段 key）
      fillSig: {},
      fillTimers: {},
      // 金额字段编辑中的原始文本（存在时优先展示，避免千分位格式化打断输入）
      moneyEditing: {},
      // 明细列宽：本次会话内拖动调整的值 fieldKey -> { colKey: width }
      colWidths: {},
      // 正在拖动列宽的列标识 fieldKey_colIndex
      resizingKey: '',
      // 成员字段关联部门字段的取值签名 fieldKey -> 上次观测到的部门取值（仅「发生变化」时才清理超范围已选值）
      deptScopeSig: {},
      // 部门字段「中心范围」选项树的实例级缓存 { base, map }：
      // 按 base（全量启用树）失效 + 按范围签名复用，保证 el-cascader 的 :options 引用稳定、面板不被反复重建
      deptTreeCache: { base: null, map: {} }
    }
  },
  computed: {
    fields() {
      let s = this.schema
      if (typeof s === 'string') {
        try { s = JSON.parse(s) } catch (e) { s = {} }
      }
      return (s && Array.isArray(s.fields)) ? s.fields : []
    },
    // 标签列宽：按 schema 中最长标签自适应（下限 110px / 上限 220px），避免中文标签折行
    labelWidth() {
      const labels = this.fields
        .filter(f => f.type !== 'tips' && f.visible !== false)
        .map(f => f.label)
      return labelWidthOf(labels)
    },
    visibleFields() {
      return this.fields.filter(f => f.type !== 'tips' && f.visible !== false)
    },
    rules() {
      const r = {}
      this.fields.forEach(f => {
        if (f.type === 'tips' || f.type === 'total' || f.type === 'sqlValue' || f.type === 'readonlyText' || f.visible === false) return
        // 必填 = 固定必填(required) 或 条件必填(requiredWhen 命中当前表单值)
        const isRequired = !!f.required || this.isRequiredNow(f)
        const arr = []
        if (f.type === 'detail') {
          // 明细：必填时至少 1 行；且每行的必填子列非空
          arr.push({
            validator: (rule, value, callback) => {
              const rows = Array.isArray(value) ? value : []
              if (isRequired && !rows.length) {
                callback(new Error((f.label || '明细') + '至少需要填写一行'))
                return
              }
              for (let i = 0; i < rows.length; i++) {
                for (const c of (f.columns || [])) {
                  if (c.type === 'ctrlNumber') {
                    const cmsg = this.ctrlColError(f, c, rows[i])
                    if (cmsg) {
                      callback(new Error(cmsg))
                      return
                    }
                  }
                  if (!c.required || c.type === 'sqlValue') continue
                  const v = rows[i] ? rows[i][c.key] : null
                  if (v == null || v === '' || (Array.isArray(v) && !v.length)) {
                    callback(new Error('明细第 ' + (i + 1) + ' 行「' + c.label + '」必填'))
                    return
                  }
                }
              }
              callback()
            },
            trigger: ['blur', 'change']
          })
        } else if (isRequired) {
          const isMulti = f.type === 'checkbox' || f.type === 'cascade' || (f.multiple && (f.type === 'user' || f.type === 'dept' || f.type === 'select'))
          arr.push({
            required: true,
            type: isMulti ? 'array' : undefined,
            message: (f.label || '该项') + '不能为空',
            trigger: ['blur', 'change']
          })
        }
        if (f.type === 'ctrlNumber') {
          arr.push({
            validator: (rule, value, callback) => {
              const msg = this.ctrlFieldError(f, value)
              if (msg) callback(new Error(msg))
              else callback()
            },
            trigger: ['blur', 'change']
          })
        }
        if (arr.length) r[f.key] = arr
      })
      return r
    },
    // 成员：部门父子索引（parentId -> 子部门 id 列表），仅纳入启用部门；
    // 仅在 deptOptions 变化时重建，避免每次过滤重复遍历部门树
    deptChildrenMap() {
      const map = {}
      ;(this.deptOptions || []).forEach(d => {
        if (!d || d.id == null || !this.deptEnabled(d)) return
        const pid = d.parentId == null ? '' : String(d.parentId)
        if (!map[pid]) map[pid] = []
        map[pid].push(d.id)
      })
      return map
    },
    // 部门控件：可选部门树（仅启用部门/中心，按 parentId 组装层级）
    // 父节点被过滤时提升为根，避免上级停用导致下级部门整体不可选
    // 注意：叶子节点不能带 children 字段。Element 用 Array.isArray(data.children) 判定是否有子级，
    // 若末级部门也写 children: []，同样会渲染展开箭头并弹出空的「暂无数据」第三列
    selectableDeptTree() {
      const map = {}
      const list = []
      ;(this.deptOptions || []).forEach(d => {
        if (!d || d.id == null || !this.deptEnabled(d)) return
        const node = { id: d.id, name: d.name, parentId: d.parentId }
        map[String(d.id)] = node
        list.push(node)
      })
      const roots = []
      list.forEach(n => {
        const parent = n.parentId == null ? null : map[String(n.parentId)]
        if (!parent) { roots.push(n); return }
        // 只在确实存在子节点时才创建 children（空数组会被判定为「有子级」）
        if (!parent.children) parent.children = []
        parent.children.push(n)
      })
      return roots
    },
    // 部门控件：el-cascader 的 props（层级选择、任意层级可选、直接存部门 id）
    // 以字段 key 缓存，保证对象引用稳定，避免每次渲染重建下拉面板
    deptCascaderPropsMap() {
      const map = {}
      this.fields.forEach(f => {
        if (f.type !== 'dept') return
        map[f.key] = {
          multiple: !!f.multiple,
          emitPath: false,
          checkStrictly: true,
          label: 'name',
          value: 'id',
          children: 'children',
          disabled: 'disabled'
        }
      })
      return map
    },
    // 部门控件：部门 id -> 父级 id 索引（用于把部门值回溯到所属的一级组织）
    deptParentMap() {
      const map = {}
      ;(this.deptOptions || []).forEach(d => {
        if (!d || d.id == null) return
        map[String(d.id)] = d.parentId == null ? '' : String(d.parentId)
      })
      return map
    },
    // 部门控件：各字段实际可用的部门树
    // - 未配置中心范围：直接用全量启用树（中心仍可勾选，保留「选中心 ⇒ 成员控件含子部门人员」的用法）
    // - 配置了中心范围：只保留所选中心，并给中心节点打 disabled（可展开、不可勾选，只能选具体部门）
    // - 兼容旧值：当前值所属的一级组织若不在范围内，临时纳入，保证旧单据编辑时能正常回显
    deptTreeMap() {
      const base = this.selectableDeptTree || []
      const cache = this.deptTreeCache || (this.deptTreeCache = { base: null, map: {} })
      // 全量树变化（如部门接口返回）时整体失效，避免复用基于旧树的选项
      if (cache.base !== base) {
        cache.base = base
        cache.map = {}
      }
      const map = {}
      this.fields.forEach(f => {
        if (!f || f.type !== 'dept') return
        const scope = Array.isArray(f.centerScope)
          ? f.centerScope.filter(x => x != null && String(x) !== '')
          : []
        if (!scope.length) {
          map[f.key] = base
          return
        }
        const keep = {}
        scope.forEach(id => { keep[String(id)] = true })
        // 兼容旧值：把当前值所属的一级组织也纳入
        const cur = this.form ? this.form[f.key] : null
        const vals = Array.isArray(cur) ? cur : (cur == null || cur === '' ? [] : [cur])
        vals.forEach(v => {
          const rootId = this.deptRootIdOf(v)
          if (rootId) keep[rootId] = true
        })
        const cacheKey = Object.keys(keep).sort().join(',')
        if (!cache.map[cacheKey]) {
          // 浅拷贝根节点并打 disabled：中心可展开不可勾选；子节点引用保持不变（只读，不会被改写）
          cache.map[cacheKey] = base
            .filter(n => keep[String(n.id)])
            .map(n => Object.assign({}, n, { disabled: true }))
        }
        map[f.key] = cache.map[cacheKey]
      })
      return map
    }
  },
  watch: {
    fields: {
      handler: 'onFieldsChange',
      immediate: true
    },
    value: {
      handler(v) {
        this.internal = true
        this.form = this.normalize(v || {})
        this.$nextTick(() => { this.internal = false })
      },
      immediate: true,
      deep: true
    },
    form: {
      handler(v) {
        this.syncComputedFields()
        this.scheduleSqlValueFetch()
        this.scheduleFillFetch()
        this.clearUnmatchedRequired()
        this.clearOutOfRangeMembers()
        if (!this.internal) this.$emit('input', this.clone(v))
      },
      deep: true
    }
  },
  methods: {
    // schema 变化：加载级联数据集 + 补自动字段(当前用户id等)，并同步回父组件 formData
    onFieldsChange() {
      this.loadCascadeDatasets()
      this.deptScopeSig = {}
      this.internal = true
      this.form = this.normalize(this.value || {})
      this.$nextTick(() => {
        this.internal = false
        this.syncComputedFields()
        this.fetchSqlValues()
        this.initFillSig()
        this.$emit('input', this.clone(this.form))
      })
    },
    // 级联：按字段加载自定义SQL数据集（按 sql 去重缓存）
    loadCascadeDatasets() {
      const sqls = {}
      this.fields.forEach(f => {
        if (f.type === 'cascade' && f.sql && f.sql.trim()) sqls[f.sql.trim()] = true
        // 明细内的级联子列同样需要加载数据集
        if (f.type === 'detail') {
          ;(f.columns || []).forEach(c => {
            if (c.type === 'cascade' && c.sql && c.sql.trim()) sqls[c.sql.trim()] = true
          })
        }
      })
      Object.keys(sqls).forEach(sql => {
        if (sql in this.cascadeRows || sql in this.cascadeFailed) return
        this.$set(this.cascadeRows, sql, [])
        queryDataset({ sql })
          .then(res => {
            if (res && res.code === 200) {
              this.$set(this.cascadeRows, sql, res.data || [])
            } else {
              this.$delete(this.cascadeRows, sql)
              this.$set(this.cascadeFailed, sql, true)
            }
          })
          .catch(() => {
            this.$delete(this.cascadeRows, sql)
            this.$set(this.cascadeFailed, sql, true)
          })
      })
    },
    // 级联：由数据集构建选项树
    cascadeOptions(f) {
      const rows = this.cascadeRows[(f.sql || '').trim()]
      return rows ? buildCascadeTree(rows, f.levels || []) : []
    },
    // 当前用户id：取当前登录用户 userId（sessionStorage 独立键 uid 优先，user 对象无 uid/id 字段）
    currentUserId() {
      const sid = window.sessionStorage.getItem('uid')
      if (sid != null && String(sid).trim() !== '') return String(sid).trim()
      const u = (this.$store && this.$store.state.currentUser) || {}
      if (u.uid != null && u.uid !== '') return u.uid
      if (u.id != null && u.id !== '') return u.id
      return ''
    },
    // 当前用户id：按 id 解析登录用户姓名（sessionStorage uid+user 优先，回退 store/原始id）
    curUserNameById(id) {
      const sid = window.sessionStorage.getItem('uid')
      if (sid && String(sid) === String(id)) {
        try {
          const su = JSON.parse(window.sessionStorage.getItem('user') || '{}')
          if (su.trueName || su.userName || su.name) return su.trueName || su.userName || su.name
        } catch (e) { /* user 解析失败时继续回退 */ }
      }
      const cu = (this.$store && this.$store.state.currentUser) || {}
      if (String(cu.uid) === String(id) || String(cu.id) === String(id)) {
        return cu.trueName || cu.userName || cu.name || String(id)
      }
      return String(id)
    },
    // 用户角色：取当前登录用户 roleId（sessionStorage 独立键 userRoleId 优先）
    currentUserRoleId() {
      const rid = window.sessionStorage.getItem('userRoleId')
      if (rid != null && String(rid).trim() !== '') return String(rid).trim()
      const u = (this.$store && this.$store.state.currentUser) || {}
      if (u.roleId != null && u.roleId !== '') return String(u.roleId)
      return ''
    },
    // 用户角色：按 roleId 解析角色名称（roleOptions 命中，未命中回退原始 roleId）
    roleNameById(id) {
      if (id == null || id === '') return ''
      const list = this.roleOptions || []
      const r = list.find(x => String(x.id) === String(id))
      if (r) return r.roleName || String(id)
      return String(id)
    },
    // 总价：计算关联明细 Σ(数量×单价)，保留2位
    computeTotal(f) {
      const rows = Array.isArray(this.form[f.detailKey]) ? this.form[f.detailKey] : []
      let sum = 0
      rows.forEach(row => {
        const q = Number(row ? row[f.qtyCol] : NaN)
        const p = Number(row ? row[f.priceCol] : NaN)
        if (!isNaN(q) && !isNaN(p)) sum += q * p
      })
      return Math.round(sum * 100) / 100
    },
    // 总价：展示文本（2位小数）
    totalDisplay(f) {
      if (!f.detailKey || !f.qtyCol || !f.priceCol) return ''
      return this.computeTotal(f).toFixed(2)
    },
    // 总价：将计算值写回表单（收敛式，值相同不写，避免循环）
    syncComputedFields() {
      this.fields.forEach(f => {
        if (f.type !== 'total' || !f.detailKey || !f.qtyCol || !f.priceCol) return
        const val = this.computeTotal(f)
        const cur = this.form[f.key]
        if (cur == null || String(cur) !== String(val)) this.$set(this.form, f.key, val)
      })
      this.syncReadonlyTextFields()
    },
    // 只读文本：把「内容模板 + ${字段key} 引用」解析后的文本写回表单值（同样走收敛式写回）
    // 编辑态实时重算；只读态仅在原值为空时兜底填充，避免历史实例因模板改动被重算覆盖
    syncReadonlyTextFields() {
      this.fields.forEach(f => {
        if (f.type !== 'readonlyText') return
        const val = this.readonlyTextValue(f)
        const cur = this.form[f.key]
        if (this.readonly) {
          if (cur == null || cur === '') this.$set(this.form, f.key, val)
          return
        }
        if (String(cur == null ? '' : cur) !== String(val)) this.$set(this.form, f.key, val)
      })
    },
    // 只读文本：渲染文本（content 中的 ${字段key} 替换为引用字段的展示值；未知 key 保留原文）
    readonlyTextValue(f) {
      const tpl = f.content == null ? '' : String(f.content)
      if (!tpl) return ''
      return tpl.replace(/\$\{([A-Za-z][A-Za-z0-9_]*)\}/g, (m, k) => this.refFieldText(k, m))
    },
    // 只读文本：单个引用的展示文本（命中表单字段时复用 displayText，保证选项/成员/金额等按业务文案回显）
    refFieldText(k, fallback) {
      const rf = this.fields.find(x => x.key === k)
      if (!rf || rf.type === 'readonlyText') return fallback || ''
      const v = this.form[k]
      if (v == null || v === '' || (Array.isArray(v) && !v.length)) return ''
      const simple = ['input', 'textarea', 'number', 'money', 'date', 'daterange', 'select', 'radio', 'checkbox',
        'cascade', 'user', 'dept', 'curUser', 'userRole', 'total', 'sqlValue', 'ctrlNumber']
      if (simple.indexOf(rf.type) >= 0) return this.displayText(rf)
      return String(Array.isArray(v) ? v.join('、') : v)
    },
    // 取值参数值换算：级联取路径叶子值，其余数组逗号拼接
    paramValue(v, srcType) {
      if (Array.isArray(v)) {
        if (srcType === 'cascade') {
          const last = v.length ? v[v.length - 1] : ''
          return last == null ? '' : String(last)
        }
        return v.join(',')
      }
      return v == null ? '' : String(v)
    },
    // 数据范围：解析字段当前生效范围（scopeRules 自上而下首个命中 → 其 scope；否则回退默认 scope）
    // 命中判定：驱动字段当前值 ∈ rule.values（字符串比较、数组任一命中）；values 为空 → 驱动字段有值即命中
    resolveScope(f) {
      const rules = (f && Array.isArray(f.scopeRules)) ? f.scopeRules : []
      for (const r of rules) {
        if (!r || !r.key) continue
        const cur = this.form ? this.form[r.key] : null
        const list = Array.isArray(cur) ? cur : [cur]
        const filled = list.filter(x => x != null && String(x) !== '')
        if (!filled.length) continue
        const values = Array.isArray(r.values) ? r.values : []
        if (!values.length) return r.scope || 'all'
        if (filled.some(x => values.some(v => String(v) === String(x)))) return r.scope || 'all'
      }
      return (f && f.scope) || 'all'
    },
    // 商机下拉列表：按字段生效数据范围选择（mine=本人商机）
    chanceOptionsFor(f) {
      return this.resolveScope(f) === 'mine' ? (this.myChanceOptions || []) : (this.chanceOptions || [])
    },
    // 项目下拉列表：按字段生效数据范围选择（mine=本人项目）
    projectOptionsFor(f) {
      return this.resolveScope(f) === 'mine' ? (this.myProjectOptions || []) : (this.projectOptions || [])
    },
    // 商机/项目选项标签：「销售账号_商机号/项目号_名称」（如 zhangj_1365_密码123）
    // saleAccount 来自后端下拉接口（商机=create_man 的 user_name；项目=其商机 create_man 的 user_name）；
    // 账号缺失（商机不存在 / 创建人已删除 / 老后端未返回该字段）时回退为「编号_名称」，避免出现前导下划线
    saleOptionLabel(item) {
      if (!item) return ''
      const id = item.id != null ? item.id : item.chanceId
      const name = item.chanceName == null ? '' : item.chanceName
      const base = String(id == null ? '' : id) + '_' + name
      const account = item.saleAccount ? String(item.saleAccount).trim() : ''
      return account ? account + '_' + base : base
    },
    // 部门控件：把一个部门值回溯到它所属的一级组织（中心）id；回溯不到返回 ''
    // 兼容多层组织：沿 parentId 逐级上溯，命中启用树的根节点即返回（值本身是中心时直接返回）
    deptRootIdOf(value) {
      const parents = this.deptParentMap || {}
      const roots = {}
      ;(this.selectableDeptTree || []).forEach(n => { if (n) roots[String(n.id)] = true })
      let cur = value == null ? '' : String(value)
      // 上限防御异常数据造成的父子成环
      for (let i = 0; cur && i < 32; i++) {
        if (roots[cur]) return cur
        const next = parents[cur]
        if (!next || next === cur) break
        cur = next
      }
      return ''
    },
    // 部门：是否启用（后端 is_valid：1=启用；0/2/NULL 视为停用；字段缺失时按启用兜底）
    deptEnabled(d) {
      if (!d) return false
      if (d.isValid === undefined) return true
      return d.isValid === 1
    },
    // 成员：解析字段关联「部门」字段当前生效的部门 id 集合（可含子部门）
    // 返 null 表示「不限制」——未配置部门范围，或关联部门字段尚未选择/已清空
    deptScopeIds(f) {
      const depKey = f && f.deptFilterKey
      if (!depKey) return null
      const cur = this.form ? this.form[depKey] : null
      const roots = (Array.isArray(cur) ? cur : [cur]).filter(x => x != null && String(x) !== '')
      if (!roots.length) return null
      const includeChildren = !f || f.deptIncludeChildren !== false
      const children = this.deptChildrenMap || {}
      const ids = new Set()
      roots.forEach(r => {
        const key = String(r)
        if (ids.has(key)) return
        ids.add(key)
        if (!includeChildren) return
        // BFS 展开子部门（部门树层级有限，队列规模可控）
        const queue = [key]
        while (queue.length) {
          const kids = children[queue.shift()] || []
          kids.forEach(cid => {
            const ck = String(cid)
            if (!ids.has(ck)) { ids.add(ck); queue.push(ck) }
          })
        }
      })
      return ids
    },
    // 成员下拉列表：配置了部门范围时按部门（含子部门）过滤，未配置或部门未选择时返回全部成员
    userOptionsFor(f) {
      const list = this.userOptions || []
      const ids = this.deptScopeIds(f)
      if (!ids) return list
      return list.filter(u => u && u.departmentId != null && ids.has(String(u.departmentId)))
    },
    // 受控数字：约束目标值（固定值/引用值；引用非数字返回 null 跳过）
    ctrlTarget(f, ct, row) {
      if (ct.mode === 'fixed') {
        const n = Number(ct.value)
        return isNaN(n) ? null : n
      }
      const s = this.resolveParam(f, ct.refKey, row || null)
      if (s == null || s === '') return null
      const n = Number(s)
      return isNaN(n) ? null : n
    },
    ctrlOpLabel(op) {
      return ({ '<': '小于', '<=': '小于等于', '>': '大于', '>=': '大于等于', '=': '等于' })[op] || op
    },
    ctrlCheck(num, ct, target) {
      switch (ct.op) {
        case '<': return num < target
        case '<=': return num <= target
        case '>': return num > target
        case '>=': return num >= target
        case '=': return num === target
        default: return true
      }
    },
    // 受控数字：字段级违例消息（空串表示通过）
    ctrlFieldError(f, value) {
      if (value == null || value === '') return ''
      const num = Number(value)
      if (isNaN(num)) return ''
      for (const ct of (f.constraints || [])) {
        const t = this.ctrlTarget(f, ct, null)
        if (t == null) continue
        if (!this.ctrlCheck(num, ct, t)) {
          const targetText = ct.mode === 'fixed' ? t : (ct.refKey + '(' + t + ')')
          return (f.label || '') + '需' + this.ctrlOpLabel(ct.op) + ' ' + targetText
        }
      }
      return ''
    },
    // 受控数字：明细子列违例消息
    ctrlColError(f, c, row) {
      const v = row ? row[c.key] : null
      if (v == null || v === '') return ''
      const num = Number(v)
      if (isNaN(num)) return ''
      for (const ct of (c.constraints || [])) {
        const t = this.ctrlTarget(f, ct, row)
        if (t == null) continue
        if (!this.ctrlCheck(num, ct, t)) {
          const targetText = ct.mode === 'fixed' ? t : (ct.refKey + '(' + t + ')')
          return '明细「' + (c.label || '') + '」需' + this.ctrlOpLabel(ct.op) + ' ' + targetText
        }
      }
      return ''
    },
    // 取值：解析 ${key}（子列key → 级联层级别名 → 表单字段key → 字段级级联别名）
    resolveParam(f, k, row) {
      // ① 行内子列key
      if (row && Object.prototype.hasOwnProperty.call(row, k)) {
        const col = (f.columns || []).find(x => x.key === k)
        return this.paramValue(row[k], col ? col.type : '')
      }
      // ② 明细级联子列层级别名（value列名→叶子值，label列名→该级label）
      for (const col of (f.columns || [])) {
        if (col.type !== 'cascade' || !row) continue
        const levels = col.levels || []
        const lv = levels.find(x => x.value === k || x.label === k)
        if (!lv) continue
        const path = Array.isArray(row[col.key]) ? row[col.key] : []
        if (!path.length) return ''
        if (lv.value === k) return String(path[path.length - 1])
        const idx = levels.findIndex(x => x.label === k)
        const labels = resolveCascadeLabels(this.cascadeOptions(col), path)
        return labels[idx] != null ? String(labels[idx]) : ''
      }
      // ③ 表单字段key
      if (Object.prototype.hasOwnProperty.call(this.form, k)) {
        const fld = this.fields.find(x => x.key === k)
        return this.paramValue(this.form[k], fld ? fld.type : '')
      }
      // ④ 字段级级联层级别名
      for (const fld of this.fields) {
        if (fld.type !== 'cascade') continue
        const levels = fld.levels || []
        const lv = levels.find(x => x.value === k || x.label === k)
        if (!lv) continue
        const path = Array.isArray(this.form[fld.key]) ? this.form[fld.key] : []
        if (!path.length) return ''
        if (lv.value === k) return String(path[path.length - 1])
        const idx = levels.findIndex(x => x.label === k)
        const labels = resolveCascadeLabels(this.cascadeOptions(fld), path)
        return labels[idx] != null ? String(labels[idx]) : ''
      }
      return ''
    },
    // 取值：提取SQL中 ${key} 引用参数
    sqlValueParams(f) {
      const re = /\$\{([A-Za-z][A-Za-z0-9_]*)\}/g
      const params = {}
      let m
      while ((m = re.exec(f.sql || ''))) {
        params[m[1]] = this.resolveParam(f, m[1], null)
      }
      return params
    },
    // 取值：全量初始取数（仅编辑态，含明细取值子列按行取数）
    fetchSqlValues() {
      if (this.readonly) return
      this.fields.forEach(f => {
        if (f.type === 'sqlValue' && f.sql && f.sql.trim()) this.fetchSqlValue(f)
        if (f.type === 'detail') {
          (f.columns || []).forEach(c => {
            if (c.type !== 'sqlValue' || !c.sql || !c.sql.trim()) return
            ;(this.form[f.key] || []).forEach((row, i) => this.fetchDetailSqlValue(f, c, i))
          })
        }
      })
    },
    // 取值：明细子列行级参数（统一走 resolveParam 解析链）
    detailSqlValueParams(f, c, row) {
      const re = /\$\{([A-Za-z][A-Za-z0-9_]*)\}/g
      const params = {}
      let m
      while ((m = re.exec(c.sql || ''))) {
        params[m[1]] = this.resolveParam(f, m[1], row)
      }
      return params
    },
    // 取值：明细子列按行取首行值写回单元格
    fetchDetailSqlValue(f, c, rowIdx) {
      const row = (this.form[f.key] || [])[rowIdx]
      if (!row) return
      const params = this.detailSqlValueParams(f, c, row)
      const sigKey = f.key + ':' + rowIdx + ':' + c.key
      this.sqlValueSig[sigKey] = JSON.stringify(params)
      querySqlValue({ sql: c.sql, params })
        .then(res => {
          if (!res || res.code !== 200) return
          const r = res.data || null
          let val = ''
          if (r) {
            const cols = Object.keys(r)
            const col = (c.valueCol && Object.prototype.hasOwnProperty.call(r, c.valueCol)) ? c.valueCol : cols[0]
            const rv = r[col]
            val = rv == null ? '' : rv
          }
          // ⚠️ 响应到达时 this.form 很可能已被「父组件 value 回环 + normalize() 深拷贝」整体替换，
          // 发请求前捕获的 row 已脱离表单，直接回写会写到旧对象上、界面读不到值
          // （表现为明细 sqlValue 列一直空白，如采购/请购明细的「单位」）。
          // 因此按行号重新取「当前」行对象；行已删除或来源参数已变化（行位移）时放弃本次写入。
          const cur = (this.form[f.key] || [])[rowIdx]
          if (!cur) return
          if (JSON.stringify(this.detailSqlValueParams(f, c, cur)) !== JSON.stringify(params)) return
          if (String(cur[c.key] == null ? '' : cur[c.key]) !== String(val)) this.$set(cur, c.key, val)
        })
        .catch(() => {})
    },
    // 取值：执行SQL取首行指定列写回表单
    fetchSqlValue(f) {
      const params = this.sqlValueParams(f)
      this.sqlValueSig[f.key] = JSON.stringify(params)
      querySqlValue({ sql: f.sql, params })
        .then(res => {
          if (!res || res.code !== 200) return
          const row = res.data || null
          let val = ''
          if (row) {
            const cols = Object.keys(row)
            const col = (f.valueCol && Object.prototype.hasOwnProperty.call(row, f.valueCol)) ? f.valueCol : cols[0]
            const rv = row[col]
            val = rv == null ? '' : rv
          }
          // 顶层字段：在「响应时刻」按 this.form 读取与写入，天然不受 value 回环替换影响
          // （明细子列需定位到具体行，故见 fetchDetailSqlValue 的「按行号重取当前行」处理）
          const cur = this.form[f.key]
          if (String(cur == null ? '' : cur) !== String(val)) this.$set(this.form, f.key, val)
        })
        .catch(() => {})
    },
    // 取值：引用参数签名变化时防抖重取（400ms，含明细取值子列按行）
    scheduleSqlValueFetch() {
      if (this.readonly) return
      this.fields.forEach(f => {
        if (f.type === 'sqlValue' && f.sql && f.sql.trim()) {
          const sig = JSON.stringify(this.sqlValueParams(f))
          if (this.sqlValueSig[f.key] === undefined) {
            this.fetchSqlValue(f)
          } else if (this.sqlValueSig[f.key] !== sig) {
            if (this.sqlValueTimers[f.key]) clearTimeout(this.sqlValueTimers[f.key])
            this.sqlValueTimers[f.key] = setTimeout(() => {
              this.sqlValueTimers[f.key] = null
              this.fetchSqlValue(f)
            }, 400)
          }
        }
        if (f.type === 'detail') {
          (f.columns || []).forEach(c => {
            if (c.type !== 'sqlValue' || !c.sql || !c.sql.trim()) return
            ;(this.form[f.key] || []).forEach((row, i) => {
              const sigKey = f.key + ':' + i + ':' + c.key
              const sig = JSON.stringify(this.detailSqlValueParams(f, c, row))
              if (this.sqlValueSig[sigKey] === undefined) {
                this.fetchDetailSqlValue(f, c, i)
              } else if (this.sqlValueSig[sigKey] !== sig) {
                if (this.sqlValueTimers[sigKey]) clearTimeout(this.sqlValueTimers[sigKey])
                this.sqlValueTimers[sigKey] = setTimeout(() => {
                  this.sqlValueTimers[sigKey] = null
                  this.fetchDetailSqlValue(f, c, i)
                }, 400)
              }
            })
          })
        }
      })
    },
    // 当前用户id：编辑态禁用框展示姓名
    curUserDisplay(f) {
      const id = this.form[f.key]
      if (id == null || id === '') return ''
      const list = this.userOptions || []
      const u = list.find(x => String(x.id) === String(id))
      if (u) return u.trueName || u.name || String(id)
      return this.curUserNameById(id)
    },
    // 用户角色：编辑态禁用框展示角色名称
    userRoleDisplay(f) {
      const id = this.form[f.key]
      if (id == null || id === '') return ''
      return this.roleNameById(id)
    },
    clone(o) {
      return JSON.parse(JSON.stringify(o == null ? {} : o))
    },
    // 依据字段类型给多值字段补默认值，避免 el-select multiple / checkbox 绑定非数组报错
    normalize(v) {
      const out = this.clone(v)
      this.fields.forEach(f => {
        const multi = f.type === 'checkbox' || f.type === 'daterange' || f.type === 'cascade' ||
          (f.multiple && (f.type === 'user' || f.type === 'dept' || f.type === 'select'))
        if (multi && !Array.isArray(out[f.key])) {
          out[f.key] = out[f.key] == null || out[f.key] === '' ? [] : out[f.key]
        }
        if (f.type === 'upload' && !Array.isArray(out[f.key])) {
          out[f.key] = out[f.key] || []
        }
        // 当前用户id：编辑态自动注入当前登录用户 userId（只读态保留原值回显）
        if (f.type === 'curUser' && !this.readonly) {
          out[f.key] = this.currentUserId()
        }
        // 用户角色：编辑态自动注入当前登录用户 roleId（隐藏也注入，只读态保留原值回显）
        if (f.type === 'userRole' && !this.readonly) {
          out[f.key] = this.currentUserRoleId()
        }
        if (f.type === 'total' && (out[f.key] == null || out[f.key] === '')) out[f.key] = 0
        // 受控数字：保证 key 存在（Vue2 响应式）
        if (f.type === 'ctrlNumber' && out[f.key] == null) out[f.key] = null
        // 只读文本：保证 key 存在（Vue2 响应式），实际值由 syncReadonlyTextFields 写入
        if (f.type === 'readonlyText' && out[f.key] == null) out[f.key] = ''
        if (f.type === 'detail') {
          if (!Array.isArray(out[f.key])) out[f.key] = []
          // 为每行补齐全部子列 key，保证 Vue2 响应式
          out[f.key] = out[f.key].map(row => {
            const r = Object.assign({}, row)
            const cols = f.columns || []
            for (const c of cols) {
              if (c.type === 'cascade') {
                if (!Array.isArray(r[c.key])) r[c.key] = []
              } else if (r[c.key] == null) {
                r[c.key] = (c.type === 'number' || c.type === 'money' || c.type === 'ctrlNumber') ? null : ''
              }
            }
            return r
          })
        }
      })
      return out
    },
    // 明细：新增一行（各子列补默认值）
    addDetailRow(f) {
      if (this.disabled) return
      const row = {}
      const cols = f.columns || []
      for (const c of cols) {
        row[c.key] = (c.type === 'number' || c.type === 'money') ? null : ''
      }
      const list = (this.form[f.key] || []).slice()
      list.push(row)
      this.$set(this.form, f.key, list)
    },
    // 明细：删除一行
    removeDetailRow(f, i) {
      if (this.disabled) return
      const list = (this.form[f.key] || []).slice()
      list.splice(i, 1)
      this.$set(this.form, f.key, list)
    },
    /* ===== 明细联动填充（detail 字段可选 fill 配置） =====
     * fill = { sourceKey, sql, dedupeKey, hintLabel }
     * 语义：sourceKey 指向的表单字段值变化后，按参数取多行追加到本明细末尾，
     *       按 dedupeKey 列的末级值去重；未配置 fill 的明细字段完全不受影响。 */
    // 填充：取出有效配置（未配置返回 null）
    fillConfig(f) {
      if (!f || f.type !== 'detail' || !f.fill) return null
      return (f.fill.sourceKey && f.fill.sql) ? f.fill : null
    },
    // 填充：初始化来源签名（仅记录，不取数；避免查看/回显历史实例时重复追加）
    initFillSig() {
      this.fields.forEach(f => {
        if (this.fillConfig(f)) this.fillSig[f.key] = JSON.stringify(this.fillParams(f))
      })
    },
    // 填充：解析 SQL 中 ${key} 引用参数（与取值字段同一解析链，级联取路径末级）
    fillParams(f) {
      const re = /\$\{([A-Za-z][A-Za-z0-9_]*)\}/g
      const params = {}
      let m
      while ((m = re.exec(f.fill.sql || ''))) {
        params[m[1]] = this.resolveParam(f, m[1], null)
      }
      return params
    },
    // 填充：来源变化后防抖取数（400ms，与取值字段一致）；来源被清空时不取数也不动明细
    scheduleFillFetch() {
      if (this.readonly || this.disabled) return
      this.fields.forEach(f => {
        if (!this.fillConfig(f)) return
        const params = this.fillParams(f)
        const sig = JSON.stringify(params)
        if (this.fillSig[f.key] === undefined) {
          this.fillSig[f.key] = sig
          return
        }
        if (this.fillSig[f.key] === sig) return
        this.fillSig[f.key] = sig
        const filled = Object.keys(params).some(k => params[k] != null && params[k] !== '')
        if (!filled) return
        if (this.fillTimers[f.key]) clearTimeout(this.fillTimers[f.key])
        this.fillTimers[f.key] = setTimeout(() => {
          this.fillTimers[f.key] = null
          this.fetchFillRows(f, params)
        }, 400)
      })
    },
    // 填充：按参数取多行
    fetchFillRows(f, params) {
      const cfg = this.fillConfig(f)
      if (!cfg) return
      queryDataset({ sql: cfg.sql, params })
        .then(res => {
          if (!res || res.code !== 200) return
          this.applyFillRows(f, res.data || [])
        })
        .catch(() => {})
    },
    // 填充：结果行 → 明细行（列名对齐明细列 key；cascade 列按末级值反查路径数组）
    applyFillRows(f, rows) {
      const cfg = this.fillConfig(f)
      if (!cfg) return
      const cols = f.columns || []
      const dedupeKey = cfg.dedupeKey
      const list = (this.form[f.key] || []).slice()
      const exists = {}
      if (dedupeKey) {
        list.forEach(r => {
          const v = this.colLeafValue(f, dedupeKey, r)
          if (v != null && v !== '') exists[String(v)] = true
        })
      }
      let added = 0
      let dup = 0
      let skipped = 0
      ;(rows || []).forEach(src => {
        // 先按列默认值铺底（与「增加明细」保持一致），再按结果集覆盖
        const row = {}
        cols.forEach(c => {
          row[c.key] = (c.type === 'number' || c.type === 'money') ? null : ''
        })
        let usable = true
        for (const c of cols) {
          const raw = src[c.key]
          if (raw === undefined) continue
          if (c.type === 'cascade') {
            const path = findCascadePath(this.cascadeOptions(c), raw)
            if (!path) { usable = false; break }
            row[c.key] = path
          } else if (c.type === 'number' || c.type === 'money') {
            row[c.key] = this.toNumberOrNull(raw)
          } else {
            row[c.key] = raw == null ? '' : String(raw)
          }
        }
        if (!usable) { skipped++; return }
        if (dedupeKey) {
          const v = this.colLeafValue(f, dedupeKey, row)
          if (v == null || v === '') { skipped++; return }
          if (exists[String(v)]) { dup++; return }
          exists[String(v)] = true
        }
        list.push(row)
        added++
      })
      if (added) this.$set(this.form, f.key, list)
      this.notifyFillResult(cfg, f, added, dup, skipped)
    },
    // 填充：明细某列的「末级值」（cascade 取路径末级，其余取原值）
    colLeafValue(f, key, row) {
      const c = (f.columns || []).find(x => x.key === key)
      return this.paramValue(row ? row[key] : null, c ? c.type : '')
    },
    // 填充：数值归一（空/非法 → null）
    toNumberOrNull(v) {
      if (v == null || v === '') return null
      const n = Number(v)
      return isNaN(n) ? null : n
    },
    // 填充：结果提示（带入 / 重复 / 跳过条数）
    notifyFillResult(cfg, f, added, dup, skipped) {
      const msgs = []
      if (added) msgs.push('已带入 ' + added + ' 行')
      if (dup) msgs.push(dup + ' 行已存在未重复添加')
      if (skipped) msgs.push(skipped + ' 行无法匹配被跳过')
      if (!msgs.length) return
      const label = cfg.hintLabel || f.label || '明细'
      const type = skipped ? 'warning' : (added ? 'success' : 'info')
      this.$message({ type, message: label + '：' + msgs.join('，') })
    },
    // 明细：列宽（优先本次会话拖动值，其次表单配置 width，均无则自动均分）
    detailColWidth(f, c) {
      const m = this.colWidths[f.key]
      if (m && m[c.key] != null) return m[c.key]
      const w = Number(c.width)
      return w > 0 ? w : null
    },
    // 明细：colgroup 列宽样式
    detailColStyle(f, c) {
      const w = this.detailColWidth(f, c)
      return w ? { width: w + 'px' } : {}
    },
    // 明细：拖动表头分隔线调整列宽（仅本次会话生效，不回写表单定义）
    startColResize(f, ci, e) {
      const c = (f.columns || [])[ci]
      if (!c) return
      const th = e.target && e.target.closest ? e.target.closest('th') : null
      const table = th && th.closest ? th.closest('table') : null
      const startX = e.clientX
      const startW = th ? th.offsetWidth : (this.detailColWidth(f, c) || 110)
      // 序号列与操作列各 44px，其余列各至少留 60px
      const others = Math.max(0, (f.columns || []).length - 1)
      const tableW = table ? table.clientWidth : 0
      const maxW = tableW ? Math.max(60, tableW - 44 * 2 - others * 60) : 1200
      const onMove = (ev) => {
        let w = Math.round(startW + (ev.clientX - startX))
        if (w < 60) w = 60
        if (w > maxW) w = maxW
        if (!this.colWidths[f.key]) this.$set(this.colWidths, f.key, {})
        this.$set(this.colWidths[f.key], c.key, w)
      }
      const onUp = () => {
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        this.resizingKey = ''
      }
      this.resizingKey = f.key + '_' + ci
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    // 明细只读单元格文本（级联解析为 label 路径，商机/项目解析为「账号_编号_名称」）
    detailCellText(c, v) {
      if (v == null || v === '' || (Array.isArray(v) && !v.length)) return '—'
      if (c.type === 'cascade') {
        const path = Array.isArray(v) ? v : [v]
        return resolveCascadeLabels(this.cascadeOptions(c), path).join(' / ') || '—'
      }
      if (c.type === 'chance') {
        const list = this.chanceOptionsFor(c)
        if (list.length) { const ch = list.find(x => x.id === v); if (ch) return this.saleOptionLabel(ch) }
        const other = this.resolveScope(c) === 'mine' ? this.chanceOptions : this.myChanceOptions
        if (other && other.length) { const ch = other.find(x => x.id === v); if (ch) return this.saleOptionLabel(ch) }
        return String(v)
      }
      if (c.type === 'project') {
        const list = this.projectOptionsFor(c)
        if (list.length) { const p = list.find(x => x.chanceId === v); if (p) return this.saleOptionLabel(p) }
        const other = this.resolveScope(c) === 'mine' ? this.projectOptions : this.myProjectOptions
        if (other && other.length) { const p = other.find(x => x.chanceId === v); if (p) return this.saleOptionLabel(p) }
        return String(v)
      }
      if (c.type === 'money') return formatMoney(v) + (c.unit ? ' ' + c.unit : '')
      return String(v)
    },
    // 条件必填：判断字段 requiredWhen 是否命中当前表单值
    // 约定 requiredWhen = { key: 依赖字段key, values: [命中取值…] }；
    // values 为空时按「依赖字段有值」判断；依赖字段为多值(数组)时任一命中即算命中
    isRequiredNow(f) {
      const cond = f && f.requiredWhen
      if (!cond || !cond.key) return false
      const cur = this.form ? this.form[cond.key] : null
      const list = Array.isArray(cur) ? cur : [cur]
      const filled = list.filter(x => x != null && String(x) !== '')
      const values = Array.isArray(cond.values)
        ? cond.values
        : (cond.value == null || cond.value === '' ? [] : [cond.value])
      if (!values.length) return filled.length > 0
      return filled.some(x => values.some(v => String(v) === String(x)))
    },
    // 条件必填：条件不再命中时清除该字段的校验残留（避免红星/红字残留）
    clearUnmatchedRequired() {
      if (!this.$refs.elForm) return
      const keys = this.fields
        .filter(f => f.requiredWhen && !this.isRequiredNow(f))
        .map(f => f.key)
      if (!keys.length) return
      this.$nextTick(() => {
        if (this.$refs.elForm) this.$refs.elForm.clearValidate(keys)
      })
    },
    // 成员：关联部门字段取值变化后，清空已选但不在范围内的成员
    // 收敛式写回：值未变化不写回；且仅在部门取值「发生变化」时清理，避免打开历史单据时误清已有取值
    clearOutOfRangeMembers() {
      if (this.readonly) return
      // 成员数据源未就绪时跳过，避免误清空已有取值
      if (!(this.userOptions || []).length) return
      this.fields.forEach(f => {
        if (!f || f.type !== 'user' || !f.deptFilterKey) return
        const raw = this.form ? this.form[f.deptFilterKey] : null
        const sig = Array.isArray(raw) ? raw.map(x => String(x)).join(',') : String(raw == null ? '' : raw)
        const seen = Object.prototype.hasOwnProperty.call(this.deptScopeSig, f.key)
        if (seen && this.deptScopeSig[f.key] === sig) return
        const changed = seen
        this.$set(this.deptScopeSig, f.key, sig)
        if (!changed) return
        const ids = this.deptScopeIds(f)
        if (!ids) return
        const cur = this.form ? this.form[f.key] : null
        const arr = Array.isArray(cur) ? cur : (cur == null || cur === '' ? [] : [cur])
        if (!arr.length) return
        const keep = arr.filter(x => {
          const u = this.userOptions.find(y => y && String(y.id) === String(x))
          return u && u.departmentId != null && ids.has(String(u.departmentId))
        })
        if (keep.length === arr.length) return
        this.$set(this.form, f.key, f.multiple ? keep : (keep.length ? keep[0] : ''))
      })
    },
    // 供父组件调用：校验表单，返回 Promise<boolean>
    validate() {
      return new Promise(resolve => {
        if (!this.$refs.elForm) return resolve(true)
        this.$refs.elForm.validate(valid => resolve(!!valid))
      })
    },
    clearValidate() {
      if (this.$refs.elForm) this.$refs.elForm.clearValidate()
    },
    fileDownloadUrl(id) {
      return (this.$store.state.IPAss.TP || '') + '/work/file-manage/download?fileId=' + id
    },
    // 只读态附件下载：走 request3（携带认证头）取文件流后触发浏览器保存
    downloadFile(file) {
      const id = file && file.id
      if (!id) {
        this.$message.warning('该附件无文件ID，无法下载')
        return
      }
      this.downloading = String(id)
      request({ url: '/work/file-manage/download?fileId=' + id, method: 'get', responseType: 'blob' })
        .then(res => {
          this.downloading = ''
          if (!res || !(res instanceof Blob)) {
            this.$message.error((res && res.msg) || '附件下载失败')
            return
          }
          // 后端异常时返回 JSON 错误体（blob 形式），解析提示
          if (res.type && res.type.indexOf('json') > -1) {
            res.text().then(t => {
              let msg = '附件下载失败'
              try { msg = JSON.parse(t).msg || msg } catch (e) { /* 保持默认提示 */ }
              this.$message.error(msg)
            })
            return
          }
          const url = window.URL.createObjectURL(res)
          const a = document.createElement('a')
          a.href = url
          a.download = file.name || ('attachment_' + id)
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          window.URL.revokeObjectURL(url)
        })
        .catch(() => {
          this.downloading = ''
          this.$message.error('附件下载失败，请检查网络或登录状态后重试')
        })
    },
    // 自定义上传：走 request3（自动携带认证头，且避免 19 位雪花 ID 精度丢失）
    customUpload(opt) {
      const fd = new FormData()
      fd.append('file', opt.file)
      request({ url: this.uploadAction, method: 'post', data: fd })
        .then(res => opt.onSuccess(res, opt.file))
        .catch(err => opt.onError(err))
    },
    // 将 el-upload 内部文件对象映射为表单值 {name,url,id}
    pickFile(x) {
      const r = x.response
      if (r && r.code === 200 && r.data) {
        const id = r.data.id
        return { name: r.data.name || x.name, id, url: id ? this.fileDownloadUrl(id) : (r.data.url || '') }
      }
      return { name: x.name, url: x.url, id: x.id }
    },
    onUploadSuccess(f, res, files) {
      if (!res || res.code !== 200) {
        this.$message.error((res && res.msg) || '附件上传失败')
      }
      const list = (files || []).filter(x => !(x.response && x.response.code !== 200))
      this.$set(this.form, f.key, list.map(x => this.pickFile(x)))
    },
    onUploadRemove(f, files) {
      this.$set(this.form, f.key, (files || []).map(x => this.pickFile(x)))
    },
    onUploadError() {
      this.$message.error('附件上传失败，请检查网络或登录状态后重试')
    },
    /* ---------------- 附件：允许的附件类型 ---------------- */
    // el-upload 的 accept 属性（未配置类型时为空串，表示不限制）
    acceptOf(f) {
      return attachAccept(f)
    },
    // 允许类型的提示文案（未配置类型时为空串）
    uploadTip(f) {
      return attachTipText(f)
    },
    // 上传前校验文件扩展名（未配置类型时直接放行）
    beforeUploadFile(f, file) {
      const exts = attachAllowedExts(f)
      if (!exts.length) return true
      const name = (file && file.name) || ''
      const idx = name.lastIndexOf('.')
      const ext = idx >= 0 ? name.slice(idx + 1).toLowerCase() : ''
      if (ext && exts.indexOf(ext) >= 0) return true
      this.$message.error('「' + name + '」格式不支持，仅允许：' + attachTipText(f))
      return false
    },
    /* ---------------- 金额：千分位 + 人民币大写 ---------------- */
    // 金额编辑缓存 key（主字段用字段 key，明细子列附加行号与列 key）
    moneyKey(f, c, ri) {
      return c ? f.key + ':' + ri + ':' + c.key : f.key
    },
    // 金额编辑框显示值：编辑中优先用原始文本，否则用千分位格式化值
    moneyValue(f) {
      if (Object.prototype.hasOwnProperty.call(this.moneyEditing, f.key)) {
        return this.moneyEditing[f.key]
      }
      const v = this.form[f.key]
      return v === '' || v === null || v === undefined ? '' : formatMoney(v)
    },
    detailMoneyValue(f, ri, c) {
      const key = this.moneyKey(f, c, ri)
      if (Object.prototype.hasOwnProperty.call(this.moneyEditing, key)) {
        return this.moneyEditing[key]
      }
      const row = (this.form[f.key] || [])[ri]
      const v = row ? row[c.key] : ''
      return v === '' || v === null || v === undefined ? '' : formatMoney(v)
    },
    hasMoneyValue(f) {
      const v = this.form[f.key]
      return !(v === '' || v === null || v === undefined)
    },
    onMoneyFocus(f) {
      const v = this.form[f.key]
      this.$set(this.moneyEditing, f.key, v === '' || v === null || v === undefined ? '' : delcommafy(String(v)))
    },
    onMoneyInput(f, raw) {
      const text = moneyInputFormatter(raw)
      this.$set(this.moneyEditing, f.key, text)
      this.$set(this.form, f.key, toMoneyNumber(text))
    },
    onMoneyBlur(f) {
      this.$delete(this.moneyEditing, f.key)
      const v = this.form[f.key]
      if (v === '' || v === null || v === undefined) return
      const n = Number(delcommafy(String(v)))
      this.$set(this.form, f.key, isNaN(n) ? '' : Number(n.toFixed(2)))
    },
    onDetailMoneyFocus(f, ri, c) {
      const row = (this.form[f.key] || [])[ri]
      const v = row ? row[c.key] : ''
      this.$set(this.moneyEditing, this.moneyKey(f, c, ri), v === '' || v === null || v === undefined ? '' : delcommafy(String(v)))
    },
    onDetailMoneyInput(f, ri, c, raw) {
      const text = moneyInputFormatter(raw)
      this.$set(this.moneyEditing, this.moneyKey(f, c, ri), text)
      const row = (this.form[f.key] || [])[ri]
      if (row) this.$set(row, c.key, toMoneyNumber(text))
    },
    onDetailMoneyBlur(f, ri, c) {
      this.$delete(this.moneyEditing, this.moneyKey(f, c, ri))
      const row = (this.form[f.key] || [])[ri]
      if (!row) return
      const v = row[c.key]
      if (v === '' || v === null || v === undefined) return
      const n = Number(delcommafy(String(v)))
      this.$set(row, c.key, isNaN(n) ? '' : Number(n.toFixed(2)))
    },
    toChineseAmount,
    // 成员（user/curUser）：userId → 姓名。命中 userOptions 取 trueName；
    // 未命中时回退 curUserNameById（可解析当前登录用户），最终回退原始 id 文本，避免只读态出现空白
    userNameText(id) {
      if (id == null || id === '') return ''
      const u = (this.userOptions || []).find(x => x && String(x.id) === String(id))
      if (u) return u.trueName || u.name || String(id)
      return this.curUserNameById(id)
    },
    // 部门（dept）：部门 id → 部门名。宽松比较 id 类型（表单值可能为字符串），未命中回退原始 id
    deptNameText(id) {
      if (id == null || id === '') return ''
      const d = (this.deptOptions || []).find(x => x && String(x.id) === String(id))
      return d ? (d.name || String(id)) : String(id)
    },
    // 只读态展示文本
    displayText(f) {
      const v = this.form[f.key]
      if (v == null || v === '') return '—'
      const opts = f.options || []
      const label = val => {
        const o = opts.find(x => String(x.value) === String(val))
        return o ? o.label : val
      }
      if (f.type === 'tips') return f.content || ''
      if (f.type === 'cascade') {
        const path = Array.isArray(v) ? v : [v]
        return resolveCascadeLabels(this.cascadeOptions(f), path).join(' / ') || '—'
      }
      // 数组值：成员/部门按名称解析（多值「、」分隔），不能落入下方基于 options 的 label 拼接
      if (Array.isArray(v)) {
        if (f.type === 'upload') return v.map(x => x.name).join('、') || '—'
        if (f.type === 'daterange') return v.filter(x => x).join(' 至 ') || '—'
        if (f.type === 'user' || f.type === 'curUser') return v.map(x => this.userNameText(x)).join('、') || '—'
        if (f.type === 'dept') return v.map(x => this.deptNameText(x)).join('、') || '—'
        return v.map(x => label(x)).join('、')
      }
      if (f.type === 'user' || f.type === 'curUser') {
        return this.userNameText(v)
      }
      if (f.type === 'dept') {
        return this.deptNameText(v)
      }
      if (f.type === 'userRole') {
        return this.roleNameById(v)
      }
      if (f.type === 'chance') {
        const list = this.chanceOptionsFor(f)
        if (list.length) { const c = list.find(x => x.id === v); if (c) return this.saleOptionLabel(c) }
        // 兜底：另一范围列表（他人实例的商机可能不在本人列表内）
        const other = this.resolveScope(f) === 'mine' ? this.chanceOptions : this.myChanceOptions
        if (other && other.length) { const c = other.find(x => x.id === v); if (c) return this.saleOptionLabel(c) }
        return v
      }
      if (f.type === 'project') {
        const list = this.projectOptionsFor(f)
        if (list.length) { const p = list.find(x => x.chanceId === v); if (p) return this.saleOptionLabel(p) }
        // 兜底：另一范围列表（他人实例的项目可能不在本人列表内）
        const other = this.resolveScope(f) === 'mine' ? this.projectOptions : this.myProjectOptions
        if (other && other.length) { const p = other.find(x => x.chanceId === v); if (p) return this.saleOptionLabel(p) }
        return v
      }
      if (f.type === 'total') {
        const n = Number(v)
        return (isNaN(n) ? 0 : n).toFixed(2) + (f.unit ? ' ' + f.unit : '')
      }
      if (f.type === 'money') return formatMoney(v) + (f.unit ? ' ' + f.unit : '')
      return label(v)
    }
  }
}
</script>

<style scoped>
.wf-form-renderer {
  width: 100%;
}
.wf-unit {
  margin-left: 8px;
  color: #909399;
  font-style: normal;
}
.wf-tips {
  color: #909399;
  font-size: 13px;
  line-height: 1.6;
  padding: 4px 0 4px 110px;
}
.wf-tips i {
  color: #409EFF;
  margin-right: 4px;
}
.wf-form-renderer .wf-ro-value {
  color: #303133;
  word-break: break-all;
}
.wf-readonly .wf-ro-value {
  color: #303133;
}
.wf-ro-files {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.wf-ro-file {
  display: flex;
  align-items: center;
  gap: 6px;
}
.wf-ro-file > i {
  color: #909399;
}
.wf-ro-file-name {
  color: #303133;
  max-width: 420px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.wf-ro-file .el-button {
  padding: 0;
}
.wf-upload-wrap {
  display: block;
}
.wf-upload-tip {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #909399;
}
.wf-money-wrap {
  display: block;
}
.wf-money {
  display: inline-flex;
  align-items: center;
}
.wf-money-upper {
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.5;
  color: #909399;
}
.wf-detail {
  width: 100%;
}
.wf-detail-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}
.wf-detail-table th,
.wf-detail-table td {
  border: 1px solid #EBEEF5;
  padding: 6px 8px;
  font-size: 12px;
  color: #606266;
  text-align: left;
  vertical-align: middle;
  word-break: break-all;
}
.wf-detail-table th {
  background: #F5F7FA;
  font-weight: 500;
  color: #303133;
  position: relative;
}
.wf-detail-col-fixed {
  width: 44px;
}
/* 表头列宽拖拽条 */
.wf-col-resizer {
  position: absolute;
  top: 0;
  right: -4px;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  z-index: 2;
  user-select: none;
}
.wf-col-resizer::after {
  content: '';
  position: absolute;
  top: 0;
  left: 3px;
  width: 2px;
  height: 100%;
  background: transparent;
}
.wf-col-resizer:hover::after,
.wf-col-resizer.active::after {
  background: #409EFF;
}
.wf-detail-idx {
  width: 44px;
  text-align: center !important;
  color: #909399;
}
.wf-detail-req {
  color: #F56C6C;
  margin-right: 2px;
  font-style: normal;
}
.wf-detail-unit {
  color: #909399;
}
.wf-detail-cell {
  min-width: 110px;
}
.wf-detail-del {
  color: #F56C6C;
  cursor: pointer;
}
.wf-detail-empty {
  text-align: center !important;
  color: #C0C4CC;
  padding: 12px 0 !important;
}
.wf-detail-add {
  margin-top: 6px;
  text-align: center;
  color: #409EFF;
  font-size: 13px;
  cursor: pointer;
  border: 1px dashed #DCDFE6;
  border-radius: 4px;
  padding: 6px 0;
}
.wf-detail-add:hover {
  border-color: #409EFF;
  background: rgba(64, 158, 255, 0.04);
}
.wf-ro-detail {
  width: 100%;
}
</style>
